import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  FormGroup,
} from "@mui/material";
import React, {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useState,
} from "react";
import { AuthContext } from "../../auth/AuthContext";
import { useAtomValue, useSetAtom } from "jotai";
import { companyDataAtom } from "../test-run/test-run-atoms";
import { useMutation } from "@tanstack/react-query";
import { cloneTestsHandler, customFirestore } from "../../firebase";
import { customAlertStateAtom } from "../../components/custom-alert";
import {
  getNestedDependencies,
  getRuns,
} from "../../atoms/test-nested-dependecies-atom";
import { type RunExecutionData } from "../../utils/types";
import { collection, doc, getDoc } from "firebase/firestore";
import { getGptDriverInput } from "../../utils/helpers";
import { getDocs } from "@firebase/firestore";

const CloneTestsDialog = ({
  open,
  setOpen,
  testIds,
}: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  testIds: string[];
}) => {
  const { user } = useContext<any>(AuthContext);
  const companyDataQuery = useAtomValue(companyDataAtom(user.companyId));

  const [selectedCompanies, setSelectedCompanies] = useState([]);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  const handleClose = useCallback(() => {
    setSelectedCompanies([]);
    setOpen(false);
  }, [setOpen]);

  const cloneMutation = useMutation({
    mutationKey: ["cloneMutationKey", selectedCompanies],
    mutationFn: async () => {
      try {
        const parsedTests: Record<string, string[]> = {};
        for (const testId of testIds) {
          const subCollectionRef = collection(
            customFirestore,
            `custom-tests/${testId}/dependencies`
          );

          const dependencies = await getDocs(subCollectionRef);

          let parsedNestedDependencies = [];

          if (!dependencies.empty) {
            const nestedDependencies = await getNestedDependencies({
              cachingEnabled: false,
              testId: dependencies.docs.at(0)!.data().testId,
              tempDependencies: [],
              platform: "irrelevant",
              device: "irrelevant",
              isSubTest: false,
            });

            console.log("getNestedDependencies", nestedDependencies);

            parsedNestedDependencies = nestedDependencies.map<RunExecutionData>(
              (dependency, index) =>
                // @ts-expect-error
                ({
                  ...dependency,
                  order: index + 1,
                  locale: "irrelevant",
                  orientation: "irrelevant",
                  os: "irrelevant",
                })
            );
            console.log("parsedNestedDependencies", parsedNestedDependencies);
          }

          const parentTest = await getDoc(
            doc(customFirestore, "custom-tests", testId)
          );

          const parentTestData = {
            ...parentTest.data(),
            id: parentTest.id,
          };
          console.log("parentTestData", parentTestData);
          const promptSteps = getGptDriverInput(parentTestData.commands);

          const runs = await getRuns({
            parsedNestedDependencies,
            steps: promptSteps,
            companyData: companyDataQuery.data!,
            sessionConfig: {
              device: "irrelevant",
              platform: "irrelevant",
              os: "irrelevant",
              language: "irrelevant",
              coordinates: "irrelevant",
              orientation: "irrelevant",
              region: "irrelevant",
            },
            isPartialExecution: false,
            test: parentTestData,
            baseline: [],
            buildData: {
              platform: "irrelevant",
              organisationId: companyDataQuery.data?.organisationId,
            },
          });

          parsedTests[testId] = runs
            .map((run) => run.testId!)
            .filter((currentTestId) => testId !== currentTestId);
        }

        console.log(parsedTests);

        await cloneTestsHandler({
          targetOrganisationIds: selectedCompanies,
          testIds: parsedTests,
        });
        setCustomAlertState({
          open: true,
          type: "success",
          title: "Success",
          description: "Tests cloned successfully",
        });
      } catch (e) {
        console.error(e);
        setCustomAlertState({
          open: true,
          type: "error",
          title: "Something went wrong",
          description: "An error occurred while cloning tests",
        });
      }
      setOpen(false);
    },
  });

  return (
    <Dialog open={open} onClose={handleClose} fullWidth={true} maxWidth={"lg"}>
      <DialogTitle>Clone tests</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Select on which sub companies you want to clone the tests.
        </DialogContentText>
        <FormGroup>
          {companyDataQuery.data?.subCompanies?.map((company) => (
            <FormControlLabel
              key={company.id}
              control={<Checkbox />}
              label={company.name}
              value={company.id}
              checked={selectedCompanies.includes(company.id)}
              onChange={(event) => {
                const companyId = event.target.value;
                setSelectedCompanies((prev) => {
                  if (prev.includes(companyId)) {
                    return prev.filter((id) => id !== companyId);
                  } else {
                    return [...prev, companyId];
                  }
                });
              }}
            />
          ))}
        </FormGroup>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          loading={cloneMutation.isPending}
          onClick={() => {
            cloneMutation.mutate();
          }}
          variant={"contained"}
          disabled={cloneMutation.isPending || selectedCompanies.length === 0}
        >
          Clone
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CloneTestsDialog;
